import { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Maximize2, ChevronLeft, ChevronRight, X, Home, DoorClosed, Square, Wrench, Droplets, Zap, Paintbrush } from 'lucide-react';
import newElevationDay from "../assets/a4f6fed3904a1101cf897479ffc7a3faf413962d.png";
import newElevationEvening from "../assets/28f027090179452c4d3f2e32bef638c99fa5b239.png";
import elevation2DSide from "../assets/48e4d162ac9414fa0ca4a9fc347eff2c7772c862.png";

const ElevationsSection = () => {
  const [selectedView, setSelectedView] = useState('day');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState({ src: '', alt: '', title: '' });

  const elevations = {
    '3d': {
      title: '2D Architectural Elevations',
      description: 'Authentic architectural renderings showcasing the precise design elements and modern luxury aesthetics of Blucon Sunrise.',
      views: {
        day: {
          title: 'Day View Elevation',
          image: newElevationDay,
          description: 'Beautiful day-facing view with modern architecture and lush landscaping'
        },
        evening: {
          title: 'Evening View Elevation', 
          image: newElevationEvening,
          description: 'Angled perspective highlighting the building\'s contemporary design and structural elements'
        },
        side: {
          title: 'Elegant Side View', 
          image: elevation2DSide,
          description: 'Angled perspective highlighting the building\'s contemporary design and structural elements'
        }
      }
    }
  };

  const openModal = (image: string, alt: string, title: string) => {
    setModalImage({ src: image, alt, title });
    setIsModalOpen(true);
  };

  const getCurrentViewData = () => {
    const section = elevations['3d'];
    return section.views[selectedView as keyof typeof section.views] || Object.values(section.views)[0];
  };

  const getViewKeys = () => {
    return Object.keys(elevations['3d'].views);
  };

  const navigateToView = (direction: 'next' | 'prev') => {
    const viewKeys = getViewKeys();
    const currentIndex = viewKeys.indexOf(selectedView);
    
    if (direction === 'next') {
      const nextIndex = (currentIndex + 1) % viewKeys.length;
      setSelectedView(viewKeys[nextIndex]);
    } else {
      const prevIndex = (currentIndex - 1 + viewKeys.length) % viewKeys.length;
      setSelectedView(viewKeys[prevIndex]);
    }
  };

  const getViewDescription = (viewKey: string) => {
    const descriptions = {
      'day': {
        title: 'Contemporary Design',
        subtitle: 'Sleek lines and stylish facades.',
        detail: 'Beautiful day-facing view with modern architecture and lush landscaping'
      },
      'evening': {
        title: 'Premium Finishes',
        subtitle: 'High-quality materials with elegant textures.',
        detail: 'Angled perspective highlighting the building\'s contemporary design and structural elements'
      },
      'side': {
        title: 'Elegant Side View',
        subtitle: 'Classic architectural lines with modern charm.',
        detail: 'Angled perspective highlighting the building\'s contemporary design and structural elements'
      }
    };
    return descriptions[viewKey as keyof typeof descriptions] || descriptions.day;
  };

  return (
    <section id="elevations" className="py-20 bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge variant="secondary" className="mb-4 bg-amber-100 text-teal-700">
            Project Elevations
          </Badge>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
           Project Details
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
           Explore the stunning architecture and high-quality specifications that define Blucon Sunrise.
          </p>
        </div>

        <Tabs defaultValue="3d" className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-lg mx-auto mb-12 bg-amber-50">
            <TabsTrigger value="3d" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
              Elevations
            </TabsTrigger>
            <TabsTrigger value="specifications" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
              Specifications
            </TabsTrigger>
          </TabsList>

          {/* Elevations Tab Content */}
          <TabsContent value="3d">
            <div className="max-w-5xl mx-auto">
              <Card className="border-0 shadow-xl shadow-amber-100/20 overflow-hidden">
                <CardContent className="p-0">
                  <div className="relative">
                    <div className={`w-full bg-[#0c2125] ${
                      selectedView === 'evening' 
                        ? 'h-[500px] lg:h-[700px] flex items-center justify-center' 
                        : 'h-96 lg:h-[600px]'
                    }`}>
                      <ImageWithFallback
                        src={getCurrentViewData().image}
                        alt={getCurrentViewData().title}
                        className={`${
                          selectedView === 'evening' 
                            ? 'w-full h-full object-contain' 
                            : 'w-full h-full object-contain'
                        }`}
                      />
                    </div>
                    
                    {/* Navigation Arrows */}
                    <Button
                      size="sm"
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-orange-500 text-white hover:bg-orange-600 shadow-lg border-0"
                      onClick={() => navigateToView('prev')}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    
                    <Button
                      size="sm"
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-orange-500 text-white hover:bg-orange-600 shadow-lg border-0"
                      onClick={() => navigateToView('next')}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </Button>

                    {/* Full Screen Button */}
                    <Button 
                      size="sm" 
                      className="absolute top-4 right-4 bg-orange-500 text-white hover:bg-orange-600 shadow-lg border-0"
                      onClick={() => openModal(
                        getCurrentViewData().image,
                        getCurrentViewData().title,
                        getCurrentViewData().title
                      )}
                    >
                      <Maximize2 className="w-4 h-4 mr-2" />
                      Full Screen
                    </Button>
                  </div>
                  
                  {/* Dynamic Descriptive Text Based on Current View */}
                  <div className="p-8">
                    <div className="text-center space-y-4">
                      <div className="space-y-2">
                        <h3 className="text-2xl font-semibold text-teal-700">
                          {getViewDescription(selectedView).title}
                        </h3>
                        <p className="text-lg text-gray-700">
                          {getViewDescription(selectedView).subtitle}
                        </p>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                          {getViewDescription(selectedView).detail}
                        </p>
                      </div>
                      
                      {/* View Indicator */}
                      <div className="flex justify-center space-x-2 mt-6">
                        {getViewKeys().map((viewKey) => (
                          <button
                            key={viewKey}
                            className={`w-3 h-3 rounded-full transition-all duration-300 ${
                              selectedView === viewKey 
                                ? 'bg-teal-600 shadow-lg' 
                                : 'bg-gray-300 hover:bg-gray-400'
                            }`}
                            onClick={() => setSelectedView(viewKey)}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Specifications Tab Content */}
          <TabsContent value="specifications">
            <div className="max-w-6xl mx-auto">
              <Card className="border-0 shadow-xl shadow-amber-100/20 overflow-visible">
                <CardContent className="p-6">
                  <div className="bg-white rounded-lg border border-gray-200 overflow-visible w-full max-w-none">
                    <div className="space-y-0">
                    
                      {/* Structure */}
                      <div className="flex items-center border-b border-gray-100 p-4 bg-white hover:bg-gray-50 transition-colors">
                        <div className="p-3 bg-blue-100 rounded-lg mr-4">
                          <Home className="w-7 h-7 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1 text-[16px]">Structure</h3>
                          <p className="text-sm text-gray-600 text-[14px]">
                            R.C.C. Framed structure with columns, beams, and slabs. AAC Blocks used for walls.
                          </p>
                        </div>
                      </div>

                      {/* Joinery & Doors */}
                      <div className="flex items-center border-b border-gray-100 p-4 bg-teal-50 hover:bg-teal-100 transition-colors">
                        <div className="p-3 bg-amber-100 rounded-lg mr-4">
                          <DoorClosed className="w-7 h-7 text-amber-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1 text-[16px]">Joinery & Doors</h3>
                          <p className="text-sm text-gray-600 text-[14px]">
                            Good quality engineered wood frames with designer hardware from YALE or equivalent. UPVC French doors with sliding shutters are provided for enhanced aesthetics and durability.
                          </p>
                        </div>
                      </div>

                      {/* Flooring */}
                      <div className="flex items-center border-b border-gray-100 p-4 bg-white hover:bg-gray-50 transition-colors">
                        <div className="p-3 bg-green-100 rounded-lg mr-4">
                          <Square className="w-7 h-7 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1 text-[16px]">Flooring</h3>
                          <p className="text-sm text-gray-600 text-[14px]">
                            The flooring features vitrified tiles in the hall, dining, and other rooms. The kitchen is equipped with a granite slab, stainless steel sink, and ceramic tile dado (2 ft height). Toilets have vitrified tiles for floor and walls (7 ft height) with concealed plumbing.
                          </p>
                        </div>
                      </div>

                      {/* Superior & Fixtures */}
                      <div className="flex items-center border-b border-gray-100 p-4 bg-teal-50 hover:bg-teal-100 transition-colors">
                        <div className="p-3 bg-purple-100 rounded-lg mr-4">
                          <Wrench className="w-7 h-7 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1 text-[16px]">Superior & Fixtures</h3>
                          <p className="text-sm text-gray-600 text-[14px]">
                            Superior ceramic brands like JAQUAR or equivalent are used. All pipelines, including CPVC, PVC soil waste, sewage, and rainwater lines, are of quality ISI brands like SENTINI or equivalent.
                          </p>
                        </div>
                      </div>

                      {/* Water Supply */}
                      <div className="flex items-center border-b border-gray-100 p-4 bg-white hover:bg-gray-50 transition-colors">
                        <div className="p-3 bg-cyan-100 rounded-lg mr-4">
                          <Droplets className="w-7 h-7 text-cyan-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1 text-[16px]">Water Supply</h3>
                          <p className="text-sm text-gray-600 text-[14px]">
                            Features one sump and an overhead tank. A dedicated tap in the kitchen for Metro water supply, while ground water is supplied to the bath, toilet, and kitchen through the overhead tank.
                          </p>
                        </div>
                      </div>

                      {/* Electrical */}
                      <div className="flex items-center border-b border-gray-100 p-4 bg-teal-50 hover:bg-teal-100 transition-colors">
                        <div className="p-3 bg-orange-100 rounded-lg mr-4">
                          <Zap className="w-7 h-7 text-orange-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1 text-[16px]">Electrical</h3>
                          <p className="text-sm text-gray-600 text-[14px]">
                            Three-phase supply connection with concealed copper wiring. Provision for internet, TV, fridge, geyser, AC, washing machine, grinder, fan, and light points in all rooms.
                          </p>
                        </div>
                      </div>

                      {/* Finishing & Terrace */}
                      <div className="flex items-center p-4 bg-white hover:bg-gray-50 transition-colors">
                        <div className="p-3 bg-pink-100 rounded-lg mr-4">
                          <Paintbrush className="w-7 h-7 text-pink-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1 text-[16px]">Finishing & Terrace</h3>
                          <p className="text-sm text-gray-600 text-[14px]">
                            Internal walls are finished with 2 coats of smooth putty, 1 coat of primer, and 2 coats of emulsion paint (ASIAN or equivalent). External walls have a texture finish, 1 coat of primer, and 2 coats of weatherproof emulsion. The terrace features weather-proof white tiles.
                          </p>
                        </div>
                      </div>

                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Full Screen Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-[85vw] max-h-[85vh] w-[85vw] h-[85vh] p-0 border-0 sm:max-w-[85vw]">
            <DialogTitle className="sr-only">
              {modalImage.title} - Full Screen View
            </DialogTitle>
            <DialogDescription className="sr-only">
              Full screen view of {modalImage.title}. Use scroll to zoom and navigate the image details.
            </DialogDescription>
            
            <div className="relative w-full h-full bg-white rounded-lg overflow-hidden">
              {/* Close Button */}
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-4 right-4 z-50 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full p-2 shadow-lg"
                onClick={() => setIsModalOpen(false)}
              >
                <X className="w-5 h-5" />
              </Button>
              
              {/* Title */}
              <div className="absolute top-4 left-4 z-50 bg-teal-600 text-white px-4 py-2 rounded-lg shadow-lg">
                <h3 className="text-lg font-semibold">{modalImage.title}</h3>
              </div>
              
              {/* Scrollable Image Container with visible scrollbars */}
              <div 
                className="w-full h-full p-8 pt-20 pb-16 overflow-auto scrollbar-visible"
                style={{
                  scrollbarWidth: 'auto',
                  scrollbarColor: '#14532d #f3f4f6'
                }}
              >
                <div className="flex justify-center">
                  <div className="relative" style={{ width: '75%', minWidth: '800px', maxWidth: '1200px' }}>
                    <ImageWithFallback
                      src={modalImage.src}
                      alt={modalImage.alt}
                      className="w-full h-auto object-contain border border-gray-200 rounded-lg shadow-sm"
                    />
                  </div>
                </div>
              </div>
              
              {/* Instructions */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-4 py-2 rounded-lg text-sm shadow-lg">
                Use scrollbars to navigate • Zoom with mouse wheel
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default ElevationsSection;